import {
  Box,
  Button,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  useMediaQuery,
  Alert,
} from "@mui/material";
import { useState, useEffect } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { getUserProfile, updateUserProfile } from "../services/api";
import Cookies from "js-cookie";
import { useParams, useNavigate } from "react-router-dom";
import { convertFromISO } from "../utils/dateValidation";
import { useTranslation } from 'react-i18next';
import { validatePhoneNumberbyCode2 } from "../utils/validation";


const Account = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [BackColor, setBackColor] = useState("");
  const [UpdateMessage, setUpdateMessage] = useState("");
  const { t } = useTranslation();
  const [phoneError, setPhoneError] = useState("");
  const [formData, setFormData] = useState({
    lastName: undefined,
    firstName: undefined,
    birthday: undefined,
    district: undefined, // Store English key directly
    phoneCode: "+852",
    phone: undefined,
    email: undefined,
    role: undefined,
  });
  const { id } = useParams();
  const isMobile = useMediaQuery("(max-width:850px)");

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
  };

  const contentStyle = {
    flex: "1 0 auto",
    position: "relative",
    width: "100%",
    paddingTop: "60px",
  };

  const catImageStyle = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center center",
    display: "block",
    transition: "transform 0.2s linear",
    willChange: "transform",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 0,
  };


  const desktopFormContainerStyle = {
    position: "absolute",
    top: "50%",
    left: "20%",
    transform: "translate(-50%, -50%)",
    width: "420px",
    padding: "75px 40px 30px",
    filter: "drop-shadow(0 5px 15px rgba(0, 0, 0, 0.1))",
    zIndex: 1,
  };

  const mobileFormContainerStyle = {
    width: "80%",
    maxWidth: "400px",
    margin: "20px auto",
    padding: "20px",
    backgroundColor: "#FCF8F3CC",
    filter: "drop-shadow(0 5px 15px rgba(0, 0, 0, 0.1))",
    zIndex: 1,
    borderRadius: "10px",
  };

  const mobileBgStyle = {
    backgroundImage: 'url("/img/login.jpeg")',
    backgroundSize: "cover",
    backgroundPosition: "70% center",
    minHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  };

  // District list - English keys only
  const districts = [
    "Central and Western District",
    "Wan Chai District",
    "Eastern District",
    "Southern District",
    "Yau Tsim Mong District",
    "Sham Shui Po District",
    "Kowloon City District",
    "Wong Tai Sin District",
    "Kwun Tong District",
    "Tsuen Wan District",
    "Tuen Mun District",
    "Yuen Long District",
    "North District",
    "Tai Po District",
    "Sha Tin District",
    "Sai Kung District",
    "Kwai Tsing District",
    "Islands District",
  ];

  // Get token from cookies
  const getUserId = () => {
    const id = Cookies.get("id");
    return id;
  };

  useEffect(() => {
    if (!getUserId()) {
      navigate("/");
    }
  }, [navigate]);

  // Fetch user profile data
  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const userId = getUserId();
      if (!userId) {
        setLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('ErrorAuthentication'));
        setTimeout(() => {
          setError(false);
        }, 2000);
        return;
      }

      const response = await getUserProfile(userId);
      // New DDD API response structure: response.data.data contains the user object
      const userData = response.data.data;

      // Parse phone number to extract country code and number
      const phoneNumber = userData.phoneNumber || "";
      let phoneCode = "+852";
      let phone = phoneNumber;

      // Check if phone number starts with a country code
      const countryCodeMatch = phoneNumber.match(/^(\+\d{2,3})/);
      if (countryCodeMatch) {
        phoneCode = countryCodeMatch[1];
        phone = phoneNumber.substring(phoneCode.length);
      }

      // Update form data with fetched user data
      setFormData({
        lastName: userData.lastName || undefined,
        firstName: userData.firstName || undefined,
        birthday: convertFromISO(userData.birthday) || undefined,
        district: districts.includes(userData.district) ? userData.district : undefined, // Validate district
        phoneCode: phoneCode,
        phone: phone || undefined,
        email: userData.email || undefined,
        role: userData.role || undefined,
      });

    } catch (err) {
      console.error("Error fetching user profile:", err);
      setLoading(false);
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorFailingLoadProfile'));
      setTimeout(() => {
        setError(false);
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  // Update user profile
  const handleUpdateProfile = async () => {
    try {
      setLoading(true);

      const userId = getUserId();
      if (!userId) {
        setLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('ErrorAuthentication'));
        setTimeout(() => {
          setError(false);
        }, 2000);
        return;
      }

      // Validate phone number
      const phoneValidationError = validatePhoneNumberbyCode2(
        { phoneNumber2: formData.phone, phoneCode: formData.phoneCode },
        t
      );

      if (phoneValidationError) {
        setLoading(false);
        setPhoneError(phoneValidationError);
        return;
      }

      setPhoneError("");

      // Convert DD/MM/YYYY to ISO string format for backend
      const convertDateForBackend = (dateString) => {
        if (!dateString) return "";
        const parts = dateString.split("/");
        if (parts.length === 3) {
          const day = parts[0];
          const month = parts[1];
          const year = parts[2];
          // Return ISO string format (YYYY-MM-DD)
          const date = new Date(year, month - 1, day);
          return date.toISOString();
        }
        return dateString;
      };

      const updateData = {
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        birthday: convertDateForBackend(formData.birthday) || undefined,
        email: formData.email || undefined,
        district: formData.district || undefined, // Already English key
        phoneNumber: formData.phone ? formData.phoneCode + formData.phone : undefined,
      };

      const response = await updateUserProfile(updateData);

      setSuccess(true);
      setBackColor("#4CAF50");
      setUpdateMessage(t('SuccessProfileUpdated'));
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    } catch (err) {
      console.error("Error updating profile:", err);
      if (err.response?.status === 409 && err.response?.data?.errorKey === "user.errors.phoneExists") {
        setLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('ExistedUser2'));
        setTimeout(() => {
          setError(false);
        }, 2000);
      }
      else if (err.response?.status === 409 && err.response?.data?.errorKey === "user.errors.emailExists") {
        setLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('ExistedUser'));
        setTimeout(() => {
          setError(false);
        }, 2000);
        return;
      }
      else {
        setLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('ErrorUpdatingProfile'));
        setTimeout(() => {
          setError(false);
        }, 2000);
      }

    } finally {
      setLoading(false);
    }
  };

  // Handle form field changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Fetch user profile on component mount
  useEffect(() => {
    fetchUserProfile();
  }, [id]);

  if (loading && !formData.email) {
    return (
      <Box
        sx={{
          bgcolor: "#fcf8f3",
        }}
      >
        {/* Header */}
        <Navbar />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Typography variant="h6" color="text.secondary">
            {/* 載入中... */}
            <img src="https://petpetclub.s3.ap-southeast-1.amazonaws.com/user-uploads/pets/68cb893a0feda12f2dbcc02b/68d4c8ce22baedfbe8b852dd.gif" alt=""
              style={{
                width: '150px',
                height: 'auto',
                mixBlendMode: 'multiply',
              }} />
            <div
              style={{
                fontWeight: "500px",
                fontSize: "15px",
                textAlign: "center",
                fontFamily: "Lucida Console",

              }}>
              {t('Loading')}
            </div>
          </Typography>
        </Box>
        <Footer />
      </Box>
    );
  }

  const renderAccountForm = () => (
    <>
      <Typography
        variant="h4"
        sx={{
          mb: { xs: 3, md: 5 },
          fontWeight: "bold",
          fontSize: { xs: "24px", sm: "28px", md: "32px" },
        }}
      >
        {t('PersonalProfile')}
      </Typography>

      <Stack
        spacing={{ xs: 4, md: 6 }}
        sx={{ maxWidth: { xs: "100%", sm: 400, md: 450 } }}
      >
        <Stack spacing={{ xs: 2.5, md: 3 }}>
          {/* Name */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                mb: { xs: 0.5, md: 1 },
                fontSize: { xs: "16px", md: "18px" },
                fontWeight: "600",
              }}
            >
              {t('LastName')}
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={formData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              InputProps={{
                sx: {
                  bgcolor: "white",
                  borderRadius: "10px",
                  height: { xs: "45px", md: "56px" },
                  fontSize: { xs: "14px", md: "16px" },
                },
              }}
            />
          </Box>

          {/* Name */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                mb: { xs: 0.5, md: 1 },
                fontSize: { xs: "16px", md: "18px" },
                fontWeight: "600",
              }}
            >
              {t('FirstName')}
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              InputProps={{
                sx: {
                  bgcolor: "white",
                  borderRadius: "10px",
                  height: { xs: "45px", md: "56px" },
                  fontSize: { xs: "14px", md: "16px" },
                },
              }}
            />
          </Box>
            {/* Phone Number */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  mb: { xs: 0.5, md: 1 },
                  fontSize: { xs: "16px", md: "18px" },
                  fontWeight: "600",
                }}
              >
                {t('phoneNumber')}
              </Typography>
              <Box sx={{ display: 'flex', gap: '10px' }}>
                <Select
                  value={formData.phoneCode}
                  onChange={(e) => handleInputChange("phoneCode", e.target.value)}
                  sx={{
                    bgcolor: "white",
                    borderRadius: "10px",
                    height: { xs: "45px", md: "56px" },
                    fontSize: { xs: "14px", md: "16px" },
                    width: { xs: "90px", md: "110px" },
                  }}
                >
                  <MenuItem value="+852">+852</MenuItem>
                  <MenuItem value="+853">+853</MenuItem>
                  <MenuItem value="+86">+86</MenuItem>
                  <MenuItem value="+865">+865</MenuItem>
                  <MenuItem value="+81">+81</MenuItem>
                  <MenuItem value="+880">+880</MenuItem>
                </Select>
                <TextField
                  fullWidth
                  type="tel"
                  placeholder={t('phoneNumber')}
                  variant="outlined"
                  value={formData.phone}
                  onChange={(e) => {
                    handleInputChange("phone", e.target.value);
                    setPhoneError("");
                  }}
                  InputProps={{
                    sx: {
                      bgcolor: "white",
                      borderRadius: "10px",
                      height: { xs: "45px", md: "56px" },
                      fontSize: { xs: "14px", md: "16px" },
                    },
                  }}
                />
              </Box>
              {phoneError && (
                <Typography
                  sx={{
                    color: "#f10000",
                    fontSize: { xs: "12px", md: "14px" },
                    mt: 0.5,
                  }}
                >
                  {phoneError}
                </Typography>
              )}
            </Box>

            {/* Email */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  mb: { xs: 0.5, md: 1 },
                  fontSize: { xs: "16px", md: "18px" },
                  fontWeight: "600",
                }}
              >
                {t('email')}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={formData.email}
                placeholder="abc@gmail.com"
                onChange={(e) => handleInputChange("email", e.target.value)}
                InputProps={{
                  sx: {
                    bgcolor: "white",
                    borderRadius: "10px",
                    height: { xs: "45px", md: "56px" },
                    fontSize: { xs: "14px", md: "16px" },
                  },
                }}
              />
            </Box>
          </Stack>

          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: { xs: "center", md: "flex-start" },
              mt: { xs: 5, md: 10 },
              mx: 3,
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              onClick={handleUpdateProfile}
              disabled={loading}
              sx={{
                backgroundColor: "#31BACD",
                color: "white",
                "&:hover": { opacity: 0.5 },
                "&:disabled": { opacity: 0.6, color: "#31BACD71" },
                minWidth: 120,
                borderRadius: "10px",
                fontSize: { xs: "14px", md: "16px" },
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center", 
                gap: "10px",
                textTransform: "none",
              }}
            >
              {t('Save')}
              {loading && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <CircularProgress size={20} color="inherit" />
                </span>
              )}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate("/pet-management")}
              sx={{
                minWidth: 120,
                borderRadius: "10px",
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
            >
              {t('cancel')}
            </Button>
          </Box>
        </Stack>
      </>
  );

  return (
    <div style={pageStyle}>
      <Navbar />
      {!isMobile ? (
        <div style={contentStyle}>
          <div style={{ position: "relative", height: "100vh" }}>
            <img
              src="/img/login.jpeg"
              alt="Account"
              style={catImageStyle}
              loading="eager"
            />
            <div style={desktopFormContainerStyle}>{renderAccountForm()}</div>
          </div>
        </div>
      ) : (
        <div style={contentStyle}>
          <div style={mobileBgStyle}>
            <div style={mobileFormContainerStyle}>{renderAccountForm()}</div>
          </div>
        </div>
      )}
      <Footer />

      {/* Notification Toast */}
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: (success || error) ? 20 : -500,
          backgroundColor: BackColor,
          color: "#fff",
          padding: isMobile ? "12px 18px" : "16px 24px",
          borderRadius: "10px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 9999,
          minWidth: 250,
          display: "flex",
          alignItems: "center",
          gap: 2,
          transition: (success || error)
            ? "right 0.4s ease-out"
            : "right 0.4s ease-in",
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {error ? (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 6L9 17L4 12"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </Box>
        <Typography fontWeight={600} fontSize={isMobile ? 12 : 16}>
          {UpdateMessage}
        </Typography>
      </Box>
    </div>
  );
};

export default Account;
